import { Fragment } from 'react';
import type { RateioDepreciacaoLinha, RateioResponse } from '@triade/contracts';
import { formatarContagem, formatarMoeda, formatarQuantidade } from '@/lib/format';
import { BotaoExpandir, NUMERO, TabelaDestinos, TD_VALOR, TH_VALOR, TotalPorCentro, useExpansao } from './comum';

/**
 * Aba Depreciacoes. As contas (qtd x unitario = total; total / vida = mensal) mostram os campos que a API devolve: a tela nao
 * recalcula nada. Sem `custo.ver` os campos de R$ nao vem e as colunas somem.
 */
export function AbaDepreciacoes({ rateio }: { rateio: RateioResponse }) {
  const { aberta, alternar } = useExpansao();
  const linhas = rateio.depreciacoes;
  const comValor = linhas.some((d) => d.valorTotal !== undefined);
  const comMensal = linhas.some((d) => d.mensal !== undefined);
  const colunas = 3 + (comValor ? 1 : 0) + (comMensal ? 1 : 0);

  if (linhas.length === 0) {
    return <p role="status" className="px-5 py-8 text-center text-small text-ink-muted">Nenhum imobilizado depreciado nesta competência.</p>;
  }

  const totalDepreciacoes = rateio.totaisAbas?.depreciacoes;
  // A ultima coluna de valor fica fixa a direita: a mensal quando existe, senao o valor total.
  const fixaValor = comMensal ? '' : ' sticky right-0 z-10 bg-surface-muted';
  const fixaCorpo = comMensal ? '' : ' sticky right-0 z-[1] bg-card';

  return (
    <div className="flex flex-col gap-4">
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full min-w-[640px] border-collapse text-small" aria-label="Depreciações rateadas">
          <thead>
            <tr className="bg-surface-muted text-left text-caption text-ink-muted">
              <th scope="col" className="w-10 px-2 py-2" aria-label="Expandir" />
              <th scope="col" className={`${TH_VALOR} sticky left-0 z-10 bg-surface-muted`}>Tipo</th>
              <th scope="col" className={TH_VALOR}>Imobilizado</th>
              {comValor && <th scope="col" className={`${TH_VALOR}${fixaValor}`}>Valor</th>}
              {comMensal && <th scope="col" className={`${TH_VALOR} sticky right-0 z-10 bg-surface-muted`}>Depreciação mensal</th>}
            </tr>
          </thead>
          <tbody>
            {linhas.map((d) => {
              const ab = aberta(d.imobilizadoId);
              return (
                <Fragment key={d.imobilizadoId}>
                  <tr data-testid="linha-depreciacao-rateio" className="border-t border-line-soft">
                    <td className="px-2 py-2.5 align-top">
                      <BotaoExpandir aberta={ab} rotulo={d.descricao} onClick={() => alternar(d.imobilizadoId)} />
                    </td>
                    <td className={`${TD_VALOR} sticky left-0 z-[1] bg-card`}>
                      <span className="rounded-md bg-primary/10 px-2 py-0.5 text-caption font-medium text-primary">{d.tipo}</span>
                    </td>
                    <td className={TD_VALOR}>{d.descricao}</td>
                    {comValor && <td className={`${TD_VALOR} tabular whitespace-nowrap${fixaCorpo}`}>{contaValor(d)}</td>}
                    {comMensal && <td className={`${TD_VALOR} tabular whitespace-nowrap sticky right-0 z-[1] bg-card`}>{contaMensal(d)}</td>}
                  </tr>
                  {ab && (
                    <tr className="bg-surface-muted">
                      <td />
                      <td colSpan={colunas - 1} className="px-3 pb-4">
                        <TabelaDestinos destinos={d.destinos} rotulo={d.descricao} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
          {comMensal && totalDepreciacoes !== undefined && (
            <tfoot>
              <tr className="border-t border-border font-semibold">
                <td colSpan={colunas - 1} className={`${TD_VALOR} bg-surface-muted`}>Total</td>
                <td className={`${TD_VALOR} ${NUMERO} sticky right-0 z-[1] bg-surface-muted`} data-testid="total-depreciacoes">{formatarMoeda(totalDepreciacoes)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <TotalPorCentro linha={rateio.mapaCif.find((l) => l.chave === 'DEPRECIACAO')} colunas={rateio.colunasMapa} testid="total-geral-depreciacoes" />
    </div>
  );
}

/** "1 × R$ 85.000,00 = R$ 85.000,00" (campos da API). */
function contaValor(d: RateioDepreciacaoLinha): string {
  if (d.valorTotal === undefined) return '—';
  return `${formatarQuantidade(d.quantidade)} × ${formatarMoeda(d.valorUnitario)} = ${formatarMoeda(d.valorTotal)}`;
}

/** "R$ 85.000,00 ÷ 240 = R$ 354,17" (campos da API). */
function contaMensal(d: RateioDepreciacaoLinha): string {
  if (d.mensal === undefined) return '—';
  return `${formatarMoeda(d.valorTotal)} ÷ ${formatarContagem(d.vidaUtilMeses)} = ${formatarMoeda(d.mensal)}`;
}
